/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import React, { useCallback, useMemo, useRef } from "react";

import { noop } from "lodash";
import { useShallow } from "zustand/react/shallow";
import TableInlineActions from "@/components/Table/actions/inlineActions/TableInlineActions/TableInlineActions";
import { useDetailsStore } from "@/stores/useDetailsStore";
import { useRedirectStore } from "@/stores/useRedirectStore";
import { useTableStoreByRoute } from "@/stores/useTableStore";
import MuiTableRow from "@mui/material/TableRow";

import { IntegrationsRow } from "../../../../../Pages/Integrations/types";
import {
  Column,
  INTEGRATION_STATUS,
  INVITATION_STATUS,
  ROUTES,
  TableRowData
} from "../../../../../shared";
import useTableItemDetails from "../../../../DetailsSection/hooks/useTableItemDetails";
import useTutorialMode from "../../../../TutorialMode/hooks/useTutorialMode";
import { STEP_GROUP } from "../../../../TutorialMode/types";
import { TableRowStyle } from "../../../styles/styleFunctions";
import TableCheckbox from "./TableCheckbox";
import TableColumn from "./TableColumn";

interface ITableRowPros {
  row: TableRowData;
  route: ROUTES;
  scrollHorizontal?: boolean;
  hasDetails: boolean;
  disabled: boolean;
  index?: number;
  companyLevelsList?: any;
  tableRowSwitch?: (rowData: IntegrationsRow) => Promise<void>;
}

const isInitiallyChecked = false;

const TableRow = ({
  row,
  route,
  scrollHorizontal,
  hasDetails,
  disabled,
  index,
  companyLevelsList,
  tableRowSwitch
}: ITableRowPros) => {
  // Select only what the row needs. useShallow keeps the object stable so the
  // row doesn't re-render unless one of these four values actually changes.
  const { columns, inlineActions, isCheckable, rowSwitch } =
    useTableStoreByRoute(
      route,
      useShallow(state => ({
        columns: state.columns,
        inlineActions: state.inlineActions,
        isCheckable: state.isCheckable,
        rowSwitch: state.rowSwitch
      }))
    );

  // Boolean: only the previously selected and newly selected rows re-render.
  const isSelectedItem = useTableStoreByRoute(
    route,
    state => state.params.selectedTableItem === row.id
  );

  const setTakeToCompanies = useRedirectStore(
    state => state.setTakeToCompanies
  );

  const setResetDetailsSection = useDetailsStore(
    state => state.setResetDetailsSection
  );

  const { checkIfCorrectTutorialStep, setTutorialDetails } = useTutorialMode();
  const { getTableItemDetails } = useTableItemDetails();

  const takeToCompaniesTriggered = useRef<boolean>(false);

  const isCompaniesPage = route === ROUTES.Companies;

  const handleDetailsClick = useCallback(
    (clickedRow: any) => {
      getTableItemDetails({
        itemId: clickedRow.id,
        updateTableRowParams: true,
        rowData: clickedRow
      });

      if (
        checkIfCorrectTutorialStep(STEP_GROUP.TutorialNewCompanyCard, 2) &&
        isCompaniesPage
      ) {
        setTutorialDetails({
          isTutorialOpen: false,
          stepIndex: 3,
          stepGroup: STEP_GROUP.TutorialNewCompanyCard,
          isTutorialActive: true
        });
      }

      setResetDetailsSection(false);
      setTakeToCompanies(null);
      takeToCompaniesTriggered.current = true;
    },
    [
      getTableItemDetails,
      setResetDetailsSection,
      setTakeToCompanies,
      isCompaniesPage,
      checkIfCorrectTutorialStep,
      setTutorialDetails
    ]
  );

  const isStatusDisabled = Boolean(
    row.disabled ||
    row?.invitationStatus === INVITATION_STATUS.Expired ||
    row.status === INTEGRATION_STATUS.Disabled
  );

  const rowCss = useMemo(
    () =>
      css([
        TableRowStyle(isInitiallyChecked, isStatusDisabled),
        { cursor: hasDetails ? "pointer" : "default" }
      ]),
    [isStatusDisabled, hasDetails]
  );

  return (
    <MuiTableRow
      data-testid="table-row"
      id={`row_${index}`}
      onClick={hasDetails ? () => handleDetailsClick(row) : noop}
      css={rowCss}
      selected={isSelectedItem}
    >
      {isCheckable && (
        <TableCheckbox
          route={route}
          fromTableRow
          isRowChecked={isInitiallyChecked}
          row={row}
          disabled={disabled}
          index={index}
          companyLevelsList={companyLevelsList}
        />
      )}

      {!isCompaniesPage &&
        columns.map(
          ({ name, resource, isSticky, secondCellValue, isActive }: Column) => {
            return (
              isActive && (
                <TableColumn
                  key={name}
                  data={row}
                  name={name}
                  isCheckable={isCheckable}
                  isSticky={isSticky}
                  resource={resource}
                  secondCellValue={secondCellValue}
                  scrollHorizontal={scrollHorizontal}
                  onToggleChange={tableRowSwitch}
                />
              )
            );
          }
        )}

      {inlineActions?.length > 0 && (
        <TableInlineActions
          scrollHorizontal={scrollHorizontal}
          inlineActions={inlineActions}
          rowSwitch={rowSwitch}
          row={row}
        />
      )}
      {/* Inline actions dialogs appears in page component (Vehicles.tsx, Drivers.tsx, etc.) */}
    </MuiTableRow>
  );
};

export default React.memo(TableRow);
